import { CartLine, SalesLine } from '@msdyn365-commerce/retail-proxy';
import { getAttrText } from './alternateWarehouseHelper';

export interface IAllowableFreightExclusionMessages {
    ltlMessage: string | undefined;
    oversizedMessage: string | undefined;
    oilMessage: string | undefined;
    allowableFreightExclusionMessage: string | undefined;
}

interface ICartLineFreightFlagHelperResources {
    allowableFreightLtlFallbackMessage: string;
    allowableFreightOversizedFallbackMessage: string;
    allowableFreightOilFallbackMessage: string;
    allowableFreightExclusionFallbackMessage: string;
}

type AllowableFreightExclusionType = 'LTL' | 'Oversized' | 'Oil' | 'AllowableFreightExcluded';

export interface IAllowableFreightExclusionResult {
    type: AllowableFreightExclusionType;
    message: string;
}

const allowableFreightTypeAttributeName = 'AllowableFreightType';

/**
 * Gets the allowable freight exclusion type from the cart line attribute values.
 * The value is expected to come from the "AllowableFreightType" cart line attribute.
 *
 * @param cartLine - The cart line containing attribute values.
 * @returns The normalized allowable freight exclusion type, or undefined if no supported type is found.
 */
const getAllowableFreightExclusionType = (cartLine: CartLine | SalesLine): AllowableFreightExclusionType | undefined => {
    const allowableFreightType = getAttrText(cartLine.AttributeValues, allowableFreightTypeAttributeName);

    switch (allowableFreightType?.trim().toLowerCase()) {
        case 'ltl':
            return 'LTL';

        case 'oversized':
            return 'Oversized';

        case 'oil':
            return 'Oil';

        case 'misc':
        case 'allowablefreightexcluded':
            return 'AllowableFreightExcluded';

        default:
            return undefined;
    }
};

/**
 * Gets the display message for the allowable freight exclusion type.
 *
 * @param exclusionType - The allowable freight exclusion type assigned to the cart line.
 * @param props - The cart line props containing app config and fallback resource strings.
 * @returns The resolved exclusion warning message, or undefined if no message should be shown.
 */
const getAllowableFreightExclusionMessage = (
    exclusionType: AllowableFreightExclusionType | undefined,
    resources: ICartLineFreightFlagHelperResources,
    configMessages: IAllowableFreightExclusionMessages | undefined
): string | undefined => {
    const {
        allowableFreightLtlFallbackMessage,
        allowableFreightOversizedFallbackMessage,
        allowableFreightOilFallbackMessage,
        allowableFreightExclusionFallbackMessage
    } = resources;

    switch (exclusionType) {
        case 'LTL':
            return configMessages?.ltlMessage || allowableFreightLtlFallbackMessage;

        case 'Oversized':
            return configMessages?.oversizedMessage || allowableFreightOversizedFallbackMessage;

        case 'Oil':
            return configMessages?.oilMessage || allowableFreightOilFallbackMessage;

        case 'AllowableFreightExcluded':
            return configMessages?.allowableFreightExclusionMessage || allowableFreightExclusionFallbackMessage;

        default:
            return undefined;
    }
};

export const getAllowableFreightExclusionResult = (
    cartLine: CartLine | SalesLine | undefined,
    resources: ICartLineFreightFlagHelperResources,
    configMessages: IAllowableFreightExclusionMessages | undefined,
    shouldEnableFreightExclusionMessages: boolean
): IAllowableFreightExclusionResult | undefined => {
    if (!shouldEnableFreightExclusionMessages || !cartLine) {
        return undefined;
    }

    const exclusionType = getAllowableFreightExclusionType(cartLine);

    if (!exclusionType) {
        return undefined;
    }

    const message = getAllowableFreightExclusionMessage(exclusionType, resources, configMessages);

    if (!message) {
        return undefined;
    }

    return {
        type: exclusionType,
        message
    };
};
